import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { dictionaries, LANGUAGES, type Lang } from './translations';

type Params = Record<string, string | number>;

interface I18nContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: string, params?: Params) => string;
  languages: typeof LANGUAGES;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

function getInitialLang(): Lang {
  const stored = localStorage.getItem('lang') as Lang | null;
  return stored === 'fr' || stored === 'en' ? stored : 'fr';
}

function interpolate(str: string, params?: Params): string {
  if (!params) return str;
  let out = str;
  for (const [k, v] of Object.entries(params)) out = out.split(`{${k}}`).join(String(v));
  return out;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(getInitialLang);
  const setLang = useCallback((l: Lang) => { localStorage.setItem('lang', l); setLangState(l); }, []);
  const t = useCallback(
    (key: string, params?: Params) => interpolate(dictionaries[lang][key] ?? key, params),
    [lang]
  );
  return <I18nContext.Provider value={{ lang, setLang, t, languages: LANGUAGES }}>{children}</I18nContext.Provider>;
}

export function useT() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useT must be used inside I18nProvider');
  return ctx;
}

export type { Lang };
